import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import {
  resolveAssignmentButtonLabel,
  resolveAssignmentDestination,
  resolveAssignmentQuestion
} from "../../../app/helpers/flowRuleI18nHelper";
import { actionRequiresComment, isReturnFlowAction } from "../../../app/helpers/flowActionVisibilityHelper";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "720px",
    width: "90%",
    maxHeight: "none",
    height: "auto",
    padding: 0,
    overflow: "visible"
  }
};

if (typeof document !== "undefined") {
  Modal.setAppElement("#root");
}

const FlowActionModal = ({
  isOpen,
  action,
  onClose,
  onAfterClose,
  onSubmit,
  modalReasonIdEnabled = false,
  pendingReasons = [],
  cancelReasons = [],
  normalReasons = [],
  ownerApprovals = [],
  isSubmitting = false,
  noteForwardEnabled = false,
  massCount = 1,
  massSummary = null,
  revealAssignee = true,
  record = null,
  massDestinationPreview = "",
  massPreviewLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const [formState, setFormState] = useState({
    reason_detail_catalog_id: "",
    reason_detail: "",
    owner_approval_catalog_id: ""
  });

  useEffect(() => {
    if (isOpen) {
      setFormState({
        reason_detail_catalog_id: "",
        reason_detail: "",
        owner_approval_catalog_id: ""
      });
    }
  }, [isOpen, action]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (onSubmit) {
      onSubmit({
        reason_detail_catalog_id: formState.reason_detail_catalog_id
          ? Number(formState.reason_detail_catalog_id)
          : null,
        reason_detail: formState.reason_detail.trim(),
        state_catalog_id: action?.target_state_catalog_id ?? null,
        owner_approval_catalog_id: formState.owner_approval_catalog_id
          ? Number(formState.owner_approval_catalog_id)
          : null
      });
    }
  };

  const title = resolveAssignmentButtonLabel(action || {}, t, i18n);
  const question = resolveAssignmentQuestion(action || {}, t, i18n);
  const singleDestination = revealAssignee
    ? resolveAssignmentDestination(action || {}, t, i18n, { record, reveal: true })
    : "";
  const destination = Number(massCount) > 1
    ? (massPreviewLoading
      ? t("flow.assignment.destination.previewLoading", "Calculando a quién se asignará cada caso…")
      : massDestinationPreview)
    : singleDestination;

  const reasonOptions = useMemo(() => {
    if (action?.cancel_reason_required) {
      return cancelReasons;
    }
    if (action?.pending_reason_required) {
      return pendingReasons;
    }
    return normalReasons;
  }, [action, pendingReasons, cancelReasons, normalReasons]);

  const showReasonCatalog = false;
  const catalogRequired = false;

  const showOwnerApproval = String(action?.value || "").trim().toLowerCase() === "end_license_verification";
  const detailRequired = actionRequiresComment(action || {}, noteForwardEnabled);
  const showComment = detailRequired;
  const isMass = Number(massCount) > 1;
  const isReturn = isReturnFlowAction(action || {});

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      onAfterClose={onAfterClose}
      style={customStyles}
      closeTimeoutMS={200}
      className="modal flow-action-modal"
      overlayClassName="modal-fondo"
    >
      <div className="modal-header d-flex flex-column gap-1 align-items-start w-100 px-3 pt-3">
        <h5 className="modal-title mb-0">
          {t("general.flow-engine.modal.title", { action: title })}
        </h5>
        {question && (
          <p className="mb-0 text-muted">{question}</p>
        )}
        {revealAssignee && destination && (
          <p className="mb-0 small text-primary" style={{ maxHeight: 180, overflowY: "auto" }}>
            {destination}
          </p>
        )}
        <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={onClose}></button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="modal-body px-3">
          {isMass && (
            <div className="mb-3">
              <p className="small mb-2">
                {t(
                  'flow.modal.mass-apply',
                  'Esta acción se aplicará a los {{count}} registros seleccionados.',
                  { count: massCount }
                )}
              </p>
              {massSummary?.mixed && (
                <div className="alert alert-warning small mb-0">
                  <strong>
                    {t(
                      'flow.mass.mixed-title',
                      'Algunos registros seleccionados se encuentran en diferentes estados'
                    )}
                  </strong>
                  <ul className="mb-0 mt-2">
                    {(massSummary.groups || []).map((group) => (
                      <li key={group.phase}>
                        {group.label}: {group.count}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!massSummary?.mixed && massSummary?.groups?.[0] && (
                <p className="small mb-0">
                  {t('flow.mass.uniform', 'Estado común:')} {massSummary.groups[0].label} ({massSummary.groups[0].count})
                </p>
              )}
            </div>
          )}
          {showReasonCatalog && (
            <div className="mb-3">
              <label className="form-label">
                {t("flow.modal.reason-catalog", "Motivo")}
              </label>
              <select
                className="form-select"
                name="reason_detail_catalog_id"
                value={formState.reason_detail_catalog_id}
                onChange={handleChange}
                required={catalogRequired}
              >
                <option value="">{t("general.option_search")}</option>
                {reasonOptions.map((reason) => (
                  <option key={reason.id || reason.value} value={reason.id || reason.value}>
                    {reason.description || reason.label || reason.name || reason.value}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showOwnerApproval && (
            <div className="mb-3">
              <label className="form-label" htmlFor="flow-owner-approval">
                {t("general.flow-engine.modal.owner-approval", "Retroalimentación del dueño")}
              </label>
              <select
                id="flow-owner-approval"
                className="form-select"
                name="owner_approval_catalog_id"
                value={formState.owner_approval_catalog_id}
                onChange={handleChange}
                required
              >
                <option value="">{t("general.option_search", "Seleccione")}</option>
                {(ownerApprovals || []).filter((item) => !item.deleted).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.description || option.name || option.value}
                  </option>
                ))}
              </select>
              <div className="form-text">
                {t(
                  "general.flow-engine.modal.owner-approval-required",
                  "Debe indicar el tipo de aprobación del dueño para cerrar la verificación."
                )}
              </div>
            </div>
          )}

          {showComment && (
          <div className="mb-3">
            <label className="form-label">
              {isReturn
                ? t("flow.motivo.label", "Motivo")
                : t("flow.modal.reason-detail", "Observación")}
            </label>
            {isMass && isReturn && (
              <p className="small text-muted mb-1">
                {t(
                  'flow.modal.mass-motivo',
                  'El mismo motivo se guardará en los {{count}} registros.',
                  { count: massCount }
                )}
              </p>
            )}
            <textarea
              className="form-control"
              name="reason_detail"
              value={formState.reason_detail}
              onChange={handleChange}
              rows={4}
              required={detailRequired}
            />
          </div>
          )}
          {!showComment && (
            <p className="text-muted small mb-0">
              {t("flow.modal.no-comment", "Esta acción no requiere observación.")}
            </p>
          )}
        </div>

        <div className="modal-footer px-3 pb-3">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
            {t("general.flow-engine.modal.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting || massSummary?.mixed}>
            {t("general.flow-engine.modal.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

FlowActionModal.propTypes = {
  isOpen: PropTypes.bool,
  action: PropTypes.object,
  onClose: PropTypes.func,
  onAfterClose: PropTypes.func,
  onSubmit: PropTypes.func,
  modalReasonIdEnabled: PropTypes.bool,
  pendingReasons: PropTypes.array,
  cancelReasons: PropTypes.array,
  normalReasons: PropTypes.array,
  ownerApprovals: PropTypes.array,
  isSubmitting: PropTypes.bool,
  noteForwardEnabled: PropTypes.bool,
  massCount: PropTypes.number,
  massSummary: PropTypes.object,
  revealAssignee: PropTypes.bool,
  record: PropTypes.object,
  massDestinationPreview: PropTypes.string,
  massPreviewLoading: PropTypes.bool
};

export default FlowActionModal;
